/**
 * Citation click telemetry
 * Fire-and-forget analytics for citation interactions
 */

interface CitationClickEvent {
  type: "external" | "memory"
  id: string // memory_id or url_hash
  source_domain?: string // For memory: domainSlug; for external: URL hostname
  position: "inline" | "pill" | "footer"
  conversation_id?: string
}

/**
 * Track citation click event
 * Non-blocking, fire-and-forget
 */
export function trackCitationClick(event: CitationClickEvent): void {
  // Fire-and-forget - don't await or block UI
  try {
    // Use existing analytics infrastructure if available
    if (typeof window !== "undefined" && (window as any).analytics?.track) {
      ;(window as any).analytics.track("citation_click", event)
    }

    // Also log for debugging in dev
    if (process.env.NODE_ENV === "development") {
      console.log("[Citation Click]", event)
    }
  } catch {
    // Silently fail - telemetry should never break UI
  }
}

/**
 * Track external link click
 */
export function trackExternalLinkClick(url: string, conversationId?: string): void {
  try {
    const hostname = new URL(url).hostname
    trackCitationClick({
      type: "external",
      id: hashString(url),
      source_domain: hostname,
      position: "inline",
      conversation_id: conversationId,
    })
  } catch {
    // Silently fail
  }
}

/**
 * Simple string hash for URL identification
 */
function hashString(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}
